import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, Gift, ShoppingBag } from "lucide-react";
import { formatPrice } from "@/lib/format";

const FREE_SHIPPING = 1500;
const SHIPPING_COST = 149;
const PROMO_ENVIO_GRATIS = new Date() <= new Date("2026-11-30T23:59:59");

export const CartDrawer = ({ open, onClose, cart, subtotal, groupPack, giftWrap, onGiftWrapChange, onUpdateQty, onRemove, onCheckout }) => {
  const giftCost = giftWrap ? 49 : 0;
  const shipping = PROMO_ENVIO_GRATIS || subtotal >= FREE_SHIPPING || subtotal === 0 ? 0 : SHIPPING_COST;
  const total = subtotal + shipping + giftCost;
  const progress = Math.min(100, (subtotal / FREE_SHIPPING) * 100);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-stone-950/50 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl dark:bg-stone-900"
          >
            <div className="flex items-center justify-between border-b border-stone-200 px-6 py-5 dark:border-stone-800">
              <h2 className="font-serif text-2xl text-stone-900 dark:text-stone-50">Tu carrito</h2>
              <button data-testid="cart-drawer-close" onClick={onClose} aria-label="Cerrar carrito" className="rounded-full p-2 text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-900 dark:hover:bg-stone-800 dark:hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="border-b border-stone-200 px-6 py-4 dark:border-stone-800">
              {PROMO_ENVIO_GRATIS ? (
                <p data-testid="free-shipping-promo" className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                  ¡Envío GRATIS en tu pedido! Promoción válida hasta el 30 de noviembre.
                </p>
              ) : subtotal >= FREE_SHIPPING ? (
                <p data-testid="free-shipping-achieved" className="text-xs font-medium text-amber-700 dark:text-amber-400">¡Tienes envío gratuito!</p>
              ) : (
                <p data-testid="free-shipping-progress-label" className="text-xs text-stone-600 dark:text-stone-300">
                  Añade <span className="font-semibold">{formatPrice(FREE_SHIPPING - subtotal)}</span> más para envío gratuito
                </p>
              )}
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-800">
                <motion.div
                  data-testid="free-shipping-progress-bar"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="h-full rounded-full bg-amber-600"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {cart.length === 0 ? (
                <div data-testid="cart-empty-state" className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <ShoppingBag size={32} className="text-stone-300 dark:text-stone-600" />
                  <p className="text-sm text-stone-500 dark:text-stone-400">Tu carrito está vacío.<br />Déjate envolver por la luz.</p>
                </div>
              ) : (
                <ul className="divide-y divide-stone-100 dark:divide-stone-800">
                  <AnimatePresence initial={false}>
                    {cart.map((item) => (
                      <motion.li
                        key={item.id}
                        layout
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 30 }}
                        data-testid={`cart-item-${item.id}`}
                        className="flex gap-4 py-4"
                      >
                        <img src={item.image} alt={item.name} className="h-20 w-16 rounded-xl object-cover" />
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-serif text-lg leading-tight text-stone-900 dark:text-stone-50">{item.name}</p>
                            <button data-testid={`cart-item-remove-${item.id}`} onClick={() => onRemove(item.id)} aria-label={`Eliminar ${item.name}`} className="p-1 text-stone-400 transition-colors hover:text-red-500">
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <p className="mt-1 text-sm font-medium text-stone-900 dark:text-stone-50">{formatPrice(item.price)}</p>
                          {item.packApplied && item.bundle && (
                            <p data-testid={`cart-item-pack-${item.id}`} className="mt-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                              Pack {item.bundle.qty}×{formatPrice(item.bundle.price)} aplicado
                            </p>
                          )}
                          <div className="mt-auto flex w-fit items-center gap-2 rounded-full border border-stone-200 px-1.5 py-0.5 dark:border-stone-700">
                            <button data-testid={`cart-item-decrement-${item.id}`} onClick={() => onUpdateQty(item.id, -1)} aria-label="Reducir" className="p-1 text-stone-500 hover:text-stone-900 dark:hover:text-white"><Minus size={12} /></button>
                            <span data-testid={`cart-item-qty-${item.id}`} className="w-5 text-center font-mono text-xs">{item.qty}</span>
                            <button data-testid={`cart-item-increment-${item.id}`} onClick={() => onUpdateQty(item.id, 1)} aria-label="Aumentar" className="p-1 text-stone-500 hover:text-stone-900 dark:hover:text-white"><Plus size={12} /></button>
                          </div>
                        </div>
                      </motion.li>
                    ))}
                  </AnimatePresence>
                </ul>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-stone-200 px-6 py-5 dark:border-stone-800">
                <label className="mb-4 flex cursor-pointer items-center gap-3 text-sm text-stone-600 dark:text-stone-300">
                  <input
                    data-testid="gift-wrap-checkbox"
                    type="checkbox"
                    checked={giftWrap}
                    onChange={(e) => onGiftWrapChange(e.target.checked)}
                    className="h-4 w-4 rounded accent-amber-600"
                  />
                  <Gift size={15} className="text-amber-600 dark:text-amber-400" />
                  Empaque de regalo sustentable (+{formatPrice(49)})
                </label>
                {groupPack && (
                  <div data-testid="cart-group-pack" className="mb-4 flex items-start gap-2.5 rounded-2xl border border-amber-500/40 bg-amber-50/60 p-3.5 dark:bg-amber-500/10">
                    <Gift size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <div>
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                        Pack del mes aplicado: {groupPack.qty} piezas por {formatPrice(groupPack.price)} · combina colores a tu gusto
                      </p>
                      <p className="mt-0.5 text-[11px] leading-snug text-amber-700/80 dark:text-amber-400/80">
                        {groupPack.label} · puedes combinar colores
                      </p>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between text-stone-500 dark:text-stone-400">
                    <span>Subtotal</span><span data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-stone-500 dark:text-stone-400">
                    <span>Envío</span><span>{shipping === 0 ? "Gratis" : formatPrice(shipping)}</span>
                  </div>
                  {giftWrap && (
                    <div className="flex justify-between text-stone-500 dark:text-stone-400">
                      <span>Empaque regalo</span><span>{formatPrice(giftCost)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-stone-200 pt-2 text-base font-semibold text-stone-900 dark:border-stone-800 dark:text-stone-50">
                    <span>Total</span><span data-testid="cart-total">{formatPrice(total)}</span>
                  </div>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={onCheckout}
                  className="mt-5 w-full rounded-full bg-stone-900 py-4 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-amber-700 dark:bg-stone-50 dark:text-stone-900 dark:hover:bg-amber-400"
                >
                  Finalizar compra
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
